import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { forEach, isEmpty, find } from "lodash";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import validate from "validate.js";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CK_CONFIGS } from "configs/ckEditor";
import { Row, Col, Form, Button, Image, Alert } from "react-bootstrap";
import EmptyState from "./component/EmptyState";
import SummaryState from "./component/SummaryState/SummaryState";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import { API_URL } from "configs";

import {
  addIncidentReview,
  getIncidentFormValues,
  getAllDepartments,
  getIncidentById,
  hideIncidentValidationError,
  updateIncidentInitiation,
  updateIncidentReview,
  getCommiteeUsersList,
  redirectToIncidentList,
} from "actions";
import { commiteeListFetch } from "actions";

const schema = {
  incident_name: {
    presence: { allowEmpty: false, message: "^Incident Title is required" },
  },
  incident_datetime: {
    presence: { allowEmpty: false, message: "^Reported Date Time is required" },
  },
  turn_around_time: {
    presence: { allowEmpty: true, message: "^Turn around time is required" },
  },
  severity_level: {
    presence: { allowEmpty: false, message: "^Severity Level is required" },
  },
  // dept_id: {
  //   presence: { allowEmpty: false, message: "^Department is required" },
  // },
};

const ReviewTab = ({
  incidentId,
  setIncidentId,
  previousData,
  onNext,
  onBack,
  loadFromBack,
  setLoadFromBack,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const session = useSelector((state) => state.session);

  const incidentState = useSelector((state) => state.incidentState);
  const commiteeState = useSelector((state) => state.commiteeState);
  const scopesState = useSelector((state) => state.scopesState);
  const user = session?.user;
  const userList = useSelector((state) => state.commiteeState.getCommiteeUsers);
  const [allScopeUsers, setAllScopeUsers] = useState([]);
  const [scopeUsersMap, setScopeUsersMap] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);

  const [severityLevelList, setSeverityLevelList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [departmentValue, setDepartmentValue] = useState([]);
  const [incidentCategoriesList, setScopeList] = useState([]);
  const [severityValue, setSeverityValue] = useState([]);
  const [scopeValue, setScopeValue] = useState([]);
  const [scopeId, setScopeId] = useState();
  const [classificationValue, setClassificationValue] = useState([]);
  const [incidentDateTime, setIncidentDateTime] = useState(new Date());
  const [isSwitchOn, setIsSwitchOn] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [prvAttachments, setPrvAttachments] = useState([]);
  const [isFinalReportGenerated, setIsFinalReportGenerated] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [incidentTimezone, setIncidentTimezone] = useState("");

  const audioRef = useRef(null);
  const isCommitteeUser = location.pathname === "/scopeIncident/edit";

  const [formState, setFormState] = useState({
    isValid: false,
    values: {
      object_viewed_id: session.current_page_permissions?.object_id || "",
      incident_datetime: incidentDateTime,
      check_switch: false,
      incident_name: "",
      turn_around_time: "",
      initiation_description: "",
    },
    touched: {
      object_viewed_id: true,
    },
    errors: {},
  });

  const valueNames = ["Severity_Level", "Scopes"];

  useEffect(() => {
    dispatch(getIncidentFormValues(valueNames));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(getAllDepartments());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   dispatch(commiteeListFetch());
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const userOptions = allScopeUsers.map((user) => ({
    label: user.name,
    value: user.id,
  }));

  useEffect(() => {
    if (location.pathname === "/scopeIncident/edit") {
      setShowPreview(true);
    }
  }, [incidentState.incidentRecord]);

  useEffect(() => {
    if (!previousData || Object.keys(previousData).length === 0) return;

    const updatedValues = {};
    const updatedTouched = {};

    if (previousData.status === "final_report") {
      setIsFinalReportGenerated(true);
      setShowPreview(true);
    }

    // Handle reported date
    const parsedDate = previousData.incident_datetime
      ? new Date(previousData.incident_datetime + "Z")
      : new Date();

    setIncidentDateTime(parsedDate);

    if (previousData.incident_timezone) {
      setIncidentTimezone(previousData.incident_timezone);
      updatedValues.incident_timezone = previousData.incident_timezone;
      updatedTouched.incident_timezone = true;
    }

    if (previousData.incident_name !== undefined) {
      updatedValues.incident_name = previousData.incident_name;
      updatedTouched.incident_name = true;
    }
    if (previousData.initiation_description !== undefined) {
      updatedValues.initiation_description =
        previousData.initiation_description;
      updatedTouched.initiation_description = true;
    }
    if (previousData.incident_datetime) {
      setFormState((prev) => ({
        ...prev,
        values: {
          ...prev.values,
          incident_datetime: parsedDate,

          turn_around_time: previousData.turn_around_time,
        },
        touched: {
          ...prev.touched,
          incident_datetime: true,

          turn_around_time: true,
        },
      }));
    }

    // Handle department selection
    if (previousData.dept_id && departmentList.length > 0) {
      const matchedDept = departmentList.find(
        (d) => d.value?.toString() === previousData.dept_id?.toString()
      );
      if (matchedDept) {
        setDepartmentValue(matchedDept);
        updatedValues.dept_id = matchedDept.value;
        updatedTouched.dept_id = true;
      }
    }

    // Handle severity level (with ID + fallback to display name)
    if (severityLevelList.length > 0) {
      let matchedSeverity = null;

      // Try matching by ID
      if (previousData.severity_level) {
        matchedSeverity = severityLevelList.find(
          (s) => s.value?.toString() === previousData.severity_level?.toString()
        );
      }

      // Fallback: try matching by display name
      if (!matchedSeverity && previousData.severity_level_name) {
        matchedSeverity = severityLevelList.find(
          (s) =>
            s.label?.toLowerCase() ===
            previousData.severity_level_name?.toLowerCase()
        );
      }

      if (matchedSeverity) {
        setSeverityValue(matchedSeverity);
        updatedValues.severity_level = matchedSeverity.value;
        updatedTouched.severity_level = true;
      }
    }

    // Handle switch
    if ("check_switch" in previousData) {
      setIsSwitchOn(previousData.check_switch === 1);
      updatedValues.check_switch = previousData.check_switch === 1;
      updatedTouched.check_switch = true;
    }

    // Handle scopes
    if (Array.isArray(previousData.scopes)) {
      const scopeIds = previousData.scopes.map((s) => s.id);
      setScopeValue(scopeIds);
      setClassificationValue(scopeIds);

      updatedValues.scope_id = scopeIds;
      updatedValues.classification = scopeIds;
      updatedTouched.scope_id = true;
      updatedTouched.classification = true;

      const fetchAllUsersFromScopes = async () => {
        let mergedUsers = [];

        for (const scopeId of scopeIds) {
          const response = await dispatch(getCommiteeUsersList(scopeId));
          const users = response?.data?.users || [];
          mergedUsers = [...mergedUsers, ...users];

          setScopeUsersMap((prevMap) => ({
            ...prevMap,
            [scopeId]: users,
          }));
        }

        const uniqueUsers = Array.from(
          new Map(mergedUsers.map((u) => [u.id, u])).values()
        );
        setAllScopeUsers(uniqueUsers);
      };

      fetchAllUsersFromScopes();
    }

    // Handle selected users
    if (Array.isArray(previousData.users)) {
      const formattedSelectedUsers = previousData.users.map((u) => ({
        label: u.name,
        value: u.id,
      }));

      setSelectedUsers(formattedSelectedUsers);
      updatedValues.user_id = formattedSelectedUsers.map((u) => u.value);
      updatedTouched.user_id = true;
    }

    // Update form state
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        ...updatedValues,
      },
      touched: {
        ...prev.touched,
        ...updatedTouched,
      },
    }));

    // Handle attachments
    if (!isEmpty(previousData.attachments)) {
      setPrvAttachments(previousData.attachments);
    }
  }, [previousData, departmentList, severityLevelList]);

  useEffect(() => {
    console.log("selectedUsers", selectedUsers);
  }, [selectedUsers]);

  // Validate form on value change
  useEffect(() => {
    const errors = validate(formState.values, schema);
    setFormState((formState) => ({
      ...formState,
      isValid: !errors,
      errors: errors || {},
    }));
  }, [formState.values]);

  useEffect(() => {
    if (!isEmpty(incidentState.validation_error)) {
      const errors = incidentState.validation_error;
      setFormState((formState) => ({
        ...formState,
        isValid: errors ? false : true,
        errors: errors || {},
      }));
    }
  }, [incidentState.validation_error]);

  useEffect(() => {
    const scopes = incidentState.incidentCategoriesList?.Scopes || [];
    const severityLevel =
      incidentState.incidentCategoriesList?.Severity_Level || [];

    console.log("severityLevel", severityLevel);

    const formattedScopes = scopes.map((item) => ({
      value: item.value,
      label: item.label,
    }));

    const formattedSeverity = severityLevel.map((item) => ({
      value: item.value,
      label: (
        <div style={{ display: "flex", alignItems: "center" }}>
          <img
            src={item.img || "/assets/images/file-icon.svg"}
            style={{ width: 12, height: 12, marginRight: 8 }}
          />
          <span>{item.label}</span>
        </div>
      ),
      rawLabel: item.label,
      description: item.description,
      responseTime: item.response_time,
    }));
    setScopeList(formattedScopes);
    setSeverityLevelList(formattedSeverity);
  }, [incidentState.incidentCategoriesList]);

  // map severity labels to hex colors
  const getSeverityColor = (level) => {
    if (!level) return "";

    switch (level.rawLabel) {
      case "High":
        return "#EAB308"; // yellow
      case "Low":
        return "#3B82F6"; // blue
      case "Medium":
        return "#22C55E"; // green
      case "Critical Timely":
        return "#F97316"; // orange
      case "Critical Immediate":
        return "#EF4444"; // red
      default:
        return ""; // no color if not matched
    }
  };

  const togglePlayPause = (fileUrl, fileId) => {
    if (playingId === fileId) {
      if (audioRef.current.paused) {
        audioRef.current.play();
        setIsPlaying(true);
      } else {
        audioRef.current.pause();
        setIsPlaying(false);
      }
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(fileUrl);
    audio.type = "audio/webm";
    audioRef.current = audio;
    audio.play().catch((err) => console.error("Audio play failed:", err));

    setPlayingId(fileId);
    setIsPlaying(true);

    audio.onended = () => {
      setPlayingId(null);
      setIsPlaying(false);
    };
  };

  useEffect(() => {
    if (Array.isArray(incidentState.departmentsDropdownList)) {
      // Map to { label, value } format for dropdowns
      const options = incidentState.departmentsDropdownList.map((dept) => ({
        label: dept.name,
        value: dept.id,
      }));
      setDepartmentList(options);
    }
  }, [incidentState.departmentsDropdownList]);

  const handleSelectChange = (selectedOption, name) => {
    // Set the selected value in respective state
    if (name === "severity_level") {
      setSeverityValue(selectedOption);
    } else if (name === "Scopes") {
      setScopeValue(selectedOption);
    }

    // Update form state
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [name]: selectedOption?.value || "",
      },
      touched: {
        ...prev.touched,
        [name]: true,
      },
    }));
  };

  const handleDepartmentChange = (option) => {
    setDepartmentValue(option);
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        dept_id: option ? option.value : "",
      },
      touched: {
        ...prev.touched,
        dept_id: true,
      },
    }));
  };

  const handleCheckboxChange = async (value, isChecked) => {
    const updatedScope = isChecked
      ? [...scopeValue, value]
      : scopeValue.filter((v) => v !== value);

    setScopeValue(updatedScope);
    setClassificationValue(updatedScope);

    let uniqueUsers = [];
    if (isChecked) {
      const response = await dispatch(getCommiteeUsersList(value));
      const newUsers = response?.data?.users || [];

      const updatedMap = {
        ...scopeUsersMap,
        [value]: newUsers,
      };
      setScopeUsersMap(updatedMap);

      const mergedUsers = Object.values(updatedMap).flat();
      uniqueUsers = Array.from(
        new Map(mergedUsers.map((u) => [u.id, u])).values()
      );
    } else {
      const updatedMap = { ...scopeUsersMap };
      delete updatedMap[value];
      setScopeUsersMap(updatedMap);

      const mergedUsers = Object.values(updatedMap).flat();
      uniqueUsers = Array.from(
        new Map(mergedUsers.map((u) => [u.id, u])).values()
      );
    }

    setAllScopeUsers(uniqueUsers);

    const formattedSelectedUsers = uniqueUsers.map((u) => ({
      label: u.name,
      value: u.id,
    }));
    setSelectedUsers(formattedSelectedUsers);

    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        scope_id: updatedScope,
        classification: updatedScope,
        user_id: uniqueUsers.map((u) => u.id),
      },
      touched: {
        ...prev.touched,
        scope_id: true,
        classification: true,
        user_id: true,
      },
    }));
  };

  // useEffect(() => {
  //   if (scopeId) {
  //     dispatch(getCommiteeUsersList(scopeId));
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [scopeId]);

  const handleChange = (event) => {
    event.persist();
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [event.target.name]: event.target.value,
      },
      touched: {
        ...prev.touched,
        [event.target.name]: true,
      },
    }));
    dispatch(hideIncidentValidationError(event.target.name));
  };

  const setDescription = (initiation_description) => {
    setFormState((formState) => ({
      ...formState,
      values: {
        ...formState.values,
        initiation_description: initiation_description,
      },
      touched: {
        ...formState.touched,
        initiation_description: true,
      },
    }));
    dispatch(hideIncidentValidationError("initiation_description"));
  };

  const handleSwitchChange = (e) => {
    const checked = e.target.checked;
    setIsSwitchOn(checked);

    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        check_switch: checked,
      },
      touched: {
        ...prev.touched,
        check_switch: true,
      },
    }));
  };

  const handleIncidentDateTimeChange = (date) => {
    setIncidentDateTime(date);
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        incident_datetime: date,
      },
      touched: {
        ...prev.touched,
        incident_datetime: true,
      },
    }));
  };

  const handleUserChange = (selectedOptions) => {
    setSelectedUsers(selectedOptions);
    const userIds = selectedOptions.map((option) => option.value);
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        user_id: userIds,
      },
    }));
  };

  const handleSeverityChange = (option) => {
    // Update local severity state
    setSeverityValue(option);

    // Update formState
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        severity_level: option.value, // store severity id/value
        turn_around_time: option.responseTime || "", // assuming API gives this key
      },
    }));

    // Also trigger your original select change handler if still needed
    handleSelectChange(option, "severity_level");
  };

  const downloadIncidentAttachment = (e, record_id) => {
    var token = localStorage.getItem("token");
    window.location.href =
      API_URL +
      "incident/downloadIncidentAttachment?id=" +
      record_id +
      "&token=" +
      token;
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();

    try {
      // Create case
      const payload = {
        incident_id: incidentId,
        ...formState.values,
        incident_datetime: formState.values.incident_datetime
          ? new Date(formState.values.incident_datetime)
              .toISOString()
              .slice(0, 19)
          : null,
      };

      const response = await dispatch(addIncidentReview(payload));

      if (response?.data?.data?.id) {
        setIncidentId(response?.data?.data?.id);
        dispatch(getIncidentById(response?.data?.data?.id, "update"));
        onNext();
      }
    } catch (error) {
      console.error("Failed to save review:", error);
    }
  };

  const handleSave = async (event) => {
    if (event) event.preventDefault();

    try {
      // Create case
      const payload = {
        incident_id: incidentId,
        ...formState.values,
        incident_datetime: formState.values.incident_datetime
          ? new Date(formState.values.incident_datetime)
              .toISOString()
              .slice(0, 19)
          : null,
      };

      const response = await dispatch(addIncidentReview(payload));

      // if (response?.data?.data?.id) {
      //   setIncidentId(response?.data?.data?.id);
      //   dispatch(getIncidentById(response?.data?.data?.id, "update"));
      //   onNext();
      // }
    } catch (error) {
      console.error("Failed to save review:", error);
    }
  };
  const hasError = (field) =>
    formState.touched[field] && formState.errors[field] ? true : false;

  if (isCommitteeUser || isFinalReportGenerated) {
    return (
      <Row>
        <Col lg={!isCommitteeUser ? 8 : 12}>
          <div className="main-content-div">
            {location.pathname !== "/scopeIncident/edit" ? (
              <div className="d-flex align-items-center justify-content-between">
                <h1 className="font-18 font-weight-bold heading-color">
                  Report Summary
                </h1>
              </div>
            ) : (
              ""
            )}

            {showPreview && <SummaryState previousData={previousData} />}
          </div>
        </Col>

        {!isCommitteeUser ? (
          <Col lg={4}>
            <div className="main-content-div mt-3">
              {prvAttachments.length > 0 && (
                <div className="attached-files-section mt-3">
                  {prvAttachments.map((file, index) => {
                    const rawFileName = file.file_name || "";
                    const fileName = rawFileName.includes("_")
                      ? rawFileName.split("_").slice(1).join("_")
                      : rawFileName;

                    const fileType = fileName.split(".").pop().toLowerCase();
                    const isImage = file.is_image === 1;
                    const isAudio = ["mp3", "wav", "ogg", "webm"].includes(
                      fileType
                    );
                    const fileUrl =
                      file.file_path || file.file_url || file.url || "#";
                    const fileId = file.id;

                    return (
                      <div
                        className="file-card-review mb-3"
                        key={`prev-${index}`}
                      >
                        <div className="file-info">
                          {isImage ? (
                            <img
                              src={fileUrl}
                              className="file-icon"
                              alt="File"
                              style={{
                                width: "30px",
                                height: "30px",
                                objectFit: "cover",
                                borderRadius: "4px",
                              }}
                              onError={(e) =>
                                (e.target.src = "/assets/images/file-icon.svg")
                              }
                            />
                          ) : isAudio ? (
                            <button
                              onClick={() => togglePlayPause(fileUrl, fileId)}
                              className="border-0 bg-transparent me-2"
                              style={{
                                width: "30px",
                                height: "30px",
                                borderRadius: "50%",
                                background: "#f0f0f0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "16px",
                              }}
                            >
                              {playingId === fileId && isPlaying ? "⏸" : "▶"}
                            </button>
                          ) : (
                            <img
                              src="/assets/images/file-icon.svg"
                              className="file-icon"
                              alt="File"
                              style={{ width: "30px", height: "30px" }}
                            />
                          )}

                          <div className="file-name-review">
                            <h6>{fileName}</h6>
                            <p>{fileType.toUpperCase()}</p>
                          </div>

                          {/* Download button */}
                          <button
                            onClick={(e) =>
                              downloadIncidentAttachment(e, fileId)
                            }
                            className="border-0 bg-transparent"
                          >
                            <Image src="/assets/images/download-icon.svg" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Col>
        ) : (
          ""
        )}

        <Col lg={12} className="mt-3">
          <button
            className="c-b-btn"
            onClick={() => {
              dispatch(redirectToIncidentList());
            }}
          >
            Back To List
          </button>
        </Col>
      </Row>
    );
  }

  return (
    <>
      <Row>
        <Col lg={9}>
          <div className="main-content-div">
            <h1 className="font-18 font-weight-bold heading-color mb-3">
              Basic Information:
            </h1>
            <Row>
              <Col lg={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">
                    Incident Title
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Incident Title"
                    className="input-field"
                    name="incident_name"
                    value={formState.values.incident_name || ""}
                    onChange={handleChange}
                    isInvalid={hasError("incident_name")}
                  />
                  <Form.Control.Feedback type="invalid">
                    xxxxxxxxxxxxl
                    {hasError("incident_name") &&
                      formState.errors.incident_name[0]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              {/* <Col lg={6}>
                <label className="label-input mb-2">Department</label> <br />
                <Select
                  options={departmentList}
                  value={departmentValue}
                  onChange={handleDepartmentChange}
                  placeholder="Select Department"
                  className="multi-select w-100"
                  isSearchable
                />
              </Col> */}
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">
                    Reported Date & Time
                  </Form.Label>
                  <div className="d-flex align-items-center">
                    <DatePicker
                      selected={incidentDateTime}
                      onChange={handleIncidentDateTimeChange}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={1}
                      dateFormat="yyyy-MM-dd HH:mm"
                      timeCaption="Time"
                      className={`form-control input-field w-100 ${
                        hasError("incident_datetime") ? "is-invalid" : ""
                      }`}
                      placeholderText="Select Date & Time"
                    />
                    {incidentTimezone && (
                      <span className="ms-2 p-2 bg-light border rounded">
                        {incidentTimezone}
                      </span>
                    )}
                  </div>
                </Form.Group>
              </Col>
              <Col lg={12}>
                <div>
                  <CKEditor
                    editor={ClassicEditor}
                    config={CK_CONFIGS(localStorage.getItem("token"))}
                    data={formState.values.initiation_description || ""}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setDescription(data);
                    }}
                  />
                  {hasError("initiation_description") && (
                    <Form.Text className="text-danger">
                      {formState.errors.initiation_description[0]}
                    </Form.Text>
                  )}
                </div>
              </Col>
              <h6 className="font-18 font-weight-bold heading-color my-3">
                Sevirity:
              </h6>
              <Col lg={12}>
                <label className="label-input mb-2">
                  Select Severity Level
                </label>{" "}
                <br />
                <Select
                  options={severityLevelList}
                  value={severityValue}
                  onChange={handleSeverityChange}
                  placeholder="Select Severity Level"
                  className="multi-select w-100"
                  isSearchable
                />
                {severityValue?.description && (
                  <Alert
                    className="mt-2 text-white mb-0 p-2 font-13 font-weight-normal border-0 d-flex align-items-center"
                    style={{ backgroundColor: getSeverityColor(severityValue) }}
                  >
                    {/* Optional icon */}
                    <img
                      src="/assets/images/alert-icon.svg"
                      alt="severity-icon"
                      style={{ width: 16, height: 16, marginRight: 8 }}
                    />

                    <span>{severityValue.description}</span>
                  </Alert>
                )}
              </Col>

              <h6 className="font-18 font-weight-bold heading-color my-3">
                Commitee:
              </h6>
              <Col lg={12}>
                <div className="main-content-div">
                  <div className="d-flex gap-5">
                    {[0, 1].map((col) => (
                      <div className="d-block" key={col}>
                        {incidentCategoriesList
                          .filter((_, i) => i % 2 === col)
                          .map((item) => (
                            <div
                              key={item.value}
                              className="d-flex align-items-center gap-2 mb-2"
                            >
                              <Form.Check
                                aria-label={`scope-${item.label}`}
                                value={item.value}
                                name="scope_id"
                                checked={scopeValue.includes(item.value)}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    item.value,
                                    e.target.checked
                                  )
                                }
                              />
                              <p className="mb-0 font-13 font-weight-normal text-color">
                                {item.label}
                              </p>
                            </div>
                          ))}
                      </div>
                    ))}
                  </div>
                </div>
              </Col>

              {/* <h6 className="font-18 font-weight-bold heading-color my-3">
                Classification:
              </h6>
              {scopeValue.length > 0 && (
                <Col lg={12}>
                  <div className="scope-content-div mt-3">
                    {scopeValue.map((id) => {
                      const matched = incidentCategoriesList.find(
                        (item) => item.value === id
                      );
                      return (
                        <p
                          key={id}
                          className="font-13 font-weight-normal text-color mb-1"
                        >
                          {matched ? matched.label : id}
                        </p>
                      );
                    })}
                  </div>
                </Col>
              )} */}

              <div className="px-3">
                <h6 className="font-18 font-weight-bold heading-color my-3">
                  Commitee Details:
                </h6>
                <div className="main-content-div mt-3">
                  <Col lg={12}>
                    {scopeValue.map((scopeId) => {
                      const committee = incidentCategoriesList.find(
                        (c) => c.value === scopeId
                      );
                      const users = scopeUsersMap[scopeId] || [];

                      if (!committee || users.length === 0) return null;

                      return (
                        <div key={scopeId} className="mb-3">
                          <h6 className="font-weight-bold mb-1">
                            {committee.label}{" "}
                            <span className="font-13 font-weight-normal text-success">
                              (Members below)
                            </span>
                          </h6>
                          <div className="scope-content-div ps-3">
                            <ul>
                              {users.map((user) => (
                                <li
                                  key={user.id}
                                  className="font-13 font-weight-normal text-color mb-1"
                                >
                                  {user.name}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      );
                    })}
                  </Col>
                </div>
              </div>

              <h6 className="font-18 font-weight-bold heading-color my-3">
                Resolution:
              </h6>
              <Col lg={12}>
                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-between">
                    <Form.Label className="label-input">
                      Turn Around Time (hrs)
                    </Form.Label>
                    <Form.Check
                      type="switch"
                      id="custom-switch"
                      className="font-13 font-weight-normal heading-color"
                      label="Override"
                      name="check_switch"
                      checked={isSwitchOn}
                      onChange={handleSwitchChange}
                    />
                  </div>
                  <Form.Control
                    type="text"
                    placeholder="Type here..."
                    name="turn_around_time"
                    className="input-field"
                    disabled={!isSwitchOn}
                    value={formState.values.turn_around_time || ""}
                    onChange={handleChange}
                    isInvalid={hasError("turn_around_time")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {hasError("turn_around_time") &&
                      formState.errors.turn_around_time[0]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex align-items-center justify-content-start gap-3">
              {/* <button className="c-b-btn" onClick={onBack}>
                {" "}
                <img
                  class="me-2"
                  src="/assets/images/back-arrow.svg"
                ></img>{" "}
                Back
              </button> */}

              <button className="save-btn" onClick={handleSave}>
                Save
              </button>
              <button
                className="n-u-btn"
                onClick={handleSubmit}
                disabled={!formState.isValid}
              >
                Next <img class="ms-2" src="/assets/images/next-icon.svg"></img>{" "}
              </button>
            </div>
          </div>
        </Col>
        {/* preview */}
        <Col lg={3}>
          <div className="main-content-div">
            <div className="d-flex align-items-center justify-content-between">
              <h1 className="font-18 font-weight-bold heading-color">
                Report Summary
              </h1>
              <button
                className="add-new-btn"
                onClick={() => setShowPreview(true)}
              >
                Preview
              </button>
            </div>

            {showPreview && <SummaryState previousData={previousData} />}
          </div>

          <div className="main-content-div mt-3">
            {prvAttachments.length > 0 && (
              <div className="attached-files-section mt-3">
                {prvAttachments.map((file, index) => {
                  const rawFileName = file.file_name || "";
                  const fileName = rawFileName.includes("_")
                    ? rawFileName.split("_").slice(1).join("_")
                    : rawFileName;

                  const fileType = fileName.split(".").pop().toLowerCase();
                  const isImage = file.is_image === 1;
                  const isAudio = ["mp3", "wav", "ogg", "webm"].includes(
                    fileType
                  );
                  const fileUrl =
                    file.file_path || file.file_url || file.url || "#";
                  const fileId = file.id;

                  return (
                    <div
                      className="file-card-review mb-3"
                      key={`prev-${index}`}
                    >
                      <div className="file-info">
                        {isImage ? (
                          <img
                            src={fileUrl}
                            className="file-icon"
                            alt="File"
                            style={{
                              width: "30px",
                              height: "30px",
                              objectFit: "cover",
                              borderRadius: "4px",
                            }}
                            onError={(e) =>
                              (e.target.src = "/assets/images/file-icon.svg")
                            }
                          />
                        ) : isAudio ? (
                          <button
                            onClick={() => togglePlayPause(fileUrl, fileId)}
                            className="border-0 bg-transparent me-2"
                            style={{
                              width: "30px",
                              height: "30px",
                              borderRadius: "50%",
                              background: "#f0f0f0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "16px",
                            }}
                          >
                            {playingId === fileId && isPlaying ? "⏸" : "▶"}
                          </button>
                        ) : (
                          <img
                            src="/assets/images/file-icon.svg"
                            className="file-icon"
                            alt="File"
                            style={{ width: "30px", height: "30px" }}
                          />
                        )}

                        <div className="file-name-review">
                          <h6>{fileName}</h6>
                          <p>{fileType.toUpperCase()}</p>
                        </div>

                        {/* Download button */}
                        <button
                          onClick={(e) => downloadIncidentAttachment(e, fileId)}
                          className="border-0 bg-transparent"
                        >
                          <Image src="/assets/images/download-icon.svg" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Col>
      </Row>
    </>
  );
};

export default ReviewTab;
